/**
 * PromptModal — full-screen centered dialog with a text input and buttons.
 *
 * Built on the same primitives as `Modal` (dim backdrop, rounded panel,
 * title text, `Button` row) but with a native `<input>` in the middle so
 * mobile users get the system keyboard and desktop users can type
 * directly. Used by the lobby "Join with code" flow instead of
 * `window.prompt()`, which looks out of place and can't be styled.
 *
 * The input is mounted via Phaser's DOM element support
 * (`scene.add.dom`), so it scales and positions with the canvas.
 * `bootGame()` enables `dom: { createContainer: true }` in the Phaser
 * game config so this "just works" for every game.
 */
import { Theme } from './Theme.js';
import { Button } from './Button.js';

export class PromptModal {
  /**
   * @param {object} opts
   * @param {Phaser.Scene} opts.scene
   * @param {string} opts.title
   * @param {string} [opts.placeholder]
   * @param {string} [opts.initialValue]
   * @param {string} [opts.inputMode='text']  - "numeric" / "tel" / "text"
   * @param {number} [opts.maxLength]
   * @param {string} [opts.pattern]            - HTML input pattern
   * @param {Array<{label: string, variant?: string, onClick: (modal: PromptModal) => void, isDefault?: boolean}>} [opts.buttons]
   * @param {(value: string, modal: PromptModal) => void} [opts.onSubmit]  - called on Enter key
   */
  constructor(opts) {
    const scene = opts.scene;
    const w = scene.scale.width;
    const h = scene.scale.height;

    this.container = scene.add.container(0, 0).setDepth(3000);

    const panelW = Math.min(460, w - 40);
    const panelH = 280;
    const panelX = w / 2;
    const panelY = h / 2;

    // Backdrop swallows clicks outside the panel.
    const dim = scene.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0.6).setInteractive();

    // Rounded panel background.
    const panel = scene.add.graphics();
    panel.fillStyle(Theme.colors.surface, 1);
    panel.fillRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, Theme.radius);
    panel.lineStyle(1, 0xffffff, 0.1);
    panel.strokeRoundedRect(panelX - panelW / 2, panelY - panelH / 2, panelW, panelH, Theme.radius);

    const title = scene.add.text(panelX, panelY - panelH / 2 + 38, opts.title, {
      fontFamily: Theme.font.family,
      fontSize: Theme.font.sizes.lg,
      color: Theme.colors.text,
      fontStyle: 'bold'
    }).setOrigin(0.5);

    this.container.add([dim, panel, title]);

    // DOM <input> in the middle of the panel.
    const inputW = panelW - 80;
    const inputStyle = [
      'width: ' + inputW + 'px',
      'padding: 14px 18px',
      'font-size: 26px',
      'font-family: ' + (Theme.font && Theme.font.family ? Theme.font.family : 'inherit'),
      'text-align: center',
      'letter-spacing: 6px',
      'color: #fff',
      'background: rgba(0, 0, 0, 0.45)',
      'border: 2px solid rgba(255, 255, 255, 0.22)',
      'border-radius: 10px',
      'outline: none',
      'box-sizing: border-box',
      '-webkit-tap-highlight-color: transparent'
    ].join('; ');

    const domEl = scene.add.dom(panelX, panelY - 10, 'input', inputStyle, opts.initialValue || '');
    const inputNode = domEl.node;
    inputNode.type = 'text';
    if (opts.inputMode) inputNode.inputMode = opts.inputMode;
    if (opts.placeholder) inputNode.placeholder = opts.placeholder;
    if (opts.maxLength) inputNode.maxLength = opts.maxLength;
    if (opts.pattern) inputNode.pattern = opts.pattern;
    inputNode.autocomplete = 'off';
    inputNode.autocapitalize = 'off';
    inputNode.autocorrect = 'off';
    inputNode.spellcheck = false;

    this._dom = domEl;
    this._input = inputNode;
    this.container.add(domEl);

    // Focus after a tick so the DOM is attached. Some browsers ignore
    // focus() on newly-created elements until they're in the layout.
    scene.time.delayedCall(50, () => {
      try { inputNode.focus(); inputNode.select(); } catch (_) { /* ignore */ }
    });

    // Keyboard shortcuts: Enter → submit / default button, Esc → close.
    const onKey = (ev) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this._submit();
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        this.close();
      }
    };
    inputNode.addEventListener('keydown', onKey);
    this._onKey = onKey;
    this._onSubmit = opts.onSubmit || null;
    this._buttons = opts.buttons || [];

    // Button row along the bottom of the panel.
    if (this._buttons.length) {
      const btnCount = this._buttons.length;
      const btnSpacing = Math.min(160, (panelW - 40) / Math.max(1, btnCount));
      const totalWidth = (btnCount - 1) * btnSpacing;
      let bx = panelX - totalWidth / 2;
      const by = panelY + panelH / 2 - 46;
      for (const b of this._buttons) {
        const btn = new Button({
          scene,
          x: bx, y: by,
          label: b.label,
          variant: b.variant || 'primary',
          onClick: () => {
            if (b.onClick) b.onClick(this);
          }
        });
        this.container.add(btn.container);
        bx += btnSpacing;
      }
    }
  }

  /** Current input value (trimmed). */
  getValue() {
    return (this._input && this._input.value || '').trim();
  }

  /** Emit the onSubmit handler (or invoke the first isDefault button). */
  _submit() {
    if (this._onSubmit) {
      this._onSubmit(this.getValue(), this);
      return;
    }
    const def = this._buttons.find((b) => b.isDefault);
    if (def && def.onClick) def.onClick(this);
  }

  /** Destroy the modal and release the input element. */
  close() {
    if (this._input && this._onKey) {
      this._input.removeEventListener('keydown', this._onKey);
    }
    if (this.container && this.container.destroy) this.container.destroy();
    this.container = null;
    this._input = null;
    this._dom = null;
  }
}
